import React, {useState, useEffect} from 'react';
import {Modal, Checkbox, Popover} from 'components/antd';
import {
  DragDropContext,
  Droppable,
  Draggable,
  DropResult,
} from 'react-beautiful-dnd';
import styled from 'styled-components';
import {Handle, DotMenuSide, Trash} from 'components/Ui/Icon';
import {
  usePoolProspectCustomizeItemsQuery,
  useUpdatePoolProspectCustomizeItemPositionMutation,
  useUpdatePoolProspectCustomizeItemIsDisplayMutation,
  useDestroyPoolProspectCustomizeItemMutation,
  PoolProspectCustomizeItem,
} from 'api';
import useProspectContext from '../useProspectContext';

interface Props {
  visible: boolean;
  setVisible: (visible: boolean) => void;
}

export default ({visible, setVisible}: Props) => {
  const {poolId} = useProspectContext();

  const [isPopoverVisible, setIsPopoverVisible] = useState(false);

  const {data: {poolProspectCustomizeItems = []} = {}} =
    usePoolProspectCustomizeItemsQuery({
      variables: {poolId: poolId},
      fetchPolicy: 'cache-and-network',
      onCompleted: () => {
        setOrderedPoolProspectCustomizeItems(poolProspectCustomizeItems);
      },
    });

  useEffect(() => {
    setOrderedPoolProspectCustomizeItems(poolProspectCustomizeItems);
  }, [poolProspectCustomizeItems.length]);

  const [
    orderedPoolProspectCustomizeItems,
    setOrderedPoolProspectCustomizeItems,
  ] = useState([]);

  const [destroyPoolProspectCustomizeItemMutation] =
    useDestroyPoolProspectCustomizeItemMutation();

  const [selectedItem, setSelectedItem] = useState('');

  const [updatePoolProspectCustomizeItemPosition] =
    useUpdatePoolProspectCustomizeItemPositionMutation();
  const [updatePoolProspectCustomizeItemIsDisplay] =
    useUpdatePoolProspectCustomizeItemIsDisplayMutation();
  const onDragEnd = React.useCallback(
    (result: DropResult) => {
      const sourceIndex = result.source.index;
      const destIndex = result.destination.index;

      const items = [...poolProspectCustomizeItems];
      const [removed] = items.splice(sourceIndex, 1);
      items.splice(destIndex, 0, removed);

      setOrderedPoolProspectCustomizeItems(items);
      updatePoolProspectCustomizeItemPosition({
        variables: {
          id: removed.id,
          position: destIndex,
        },
      });
    },
    [orderedPoolProspectCustomizeItems],
  );

  const Content = (item: PoolProspectCustomizeItem) => {
    return (
      <PopoverContent>
        <PopoverItem
          onClick={() => {
            if (item.formBlockFieldCustomizeItems?.length === 0) {
              setIsPopoverVisible(false);
              destroyPoolProspectCustomizeItemMutation({
                variables: {id: item.id},
              });
            } else {
              alert('フォーム機能で使われている項目の為、削除できません。');
            }
          }}>
          <TrashWrapper>
            <Trash color="var(--text-color-1)" />
          </TrashWrapper>
          <div>削除</div>
        </PopoverItem>
      </PopoverContent>
    );
  };

  return (
    <Modal
      title={'項目の設定'}
      visible={visible}
      onCancel={() => setVisible(false)}
      okText={'保存'}
      cancelText={'キャンセル'}
      footer={null}
      width={500}>
      <HeaderContentWrapper>
        <Gap width={24} />
        <ItemName>
          <Bold>項目名</Bold>
        </ItemName>
        <Gap width={123} />
        <DataType>
          <Bold>データ形式</Bold>
        </DataType>
        <Gap width={22} />
        <Display>
          <Bold>表示</Bold>
        </Display>
        <Gap width={20} />
      </HeaderContentWrapper>
      <DragDropContext onDragEnd={onDragEnd}>
        <Droppable droppableId="droppable">
          {(provided) => (
            <div {...provided.droppableProps} ref={provided.innerRef}>
              {orderedPoolProspectCustomizeItems
                .filter(
                  (item: PoolProspectCustomizeItem) => item.isDeleted === false,
                )
                .map((item: any, index: number) => (
                  <Draggable key={item.id} draggableId={item.id} index={index}>
                    {(provided, snapshot) => (
                      <DraggableItem
                        ref={provided.innerRef}
                        {...provided.draggableProps}
                        className={snapshot.isDragging ? 'dragging' : ''}>
                        <HandleWrapper {...provided.dragHandleProps}>
                          <Handle />
                        </HandleWrapper>
                        <ContentWrapper>
                          <ItemName>{item.name}</ItemName>
                          <Gap width={123} />
                          <DataType>{item.dataTypeText}</DataType>
                          <Gap width={22} />
                          <Display>
                            <Checkbox
                              defaultChecked={item.isDisplay}
                              onChange={(e) => {
                                updatePoolProspectCustomizeItemIsDisplay({
                                  variables: {
                                    id: item.id,
                                    isDisplay: e.target.checked,
                                  },
                                  refetchQueries: [
                                    'poolProspectCustomizeItems',
                                  ],
                                });
                              }}
                            />
                          </Display>
                          <Popover
                            placement="bottomLeft"
                            content={Content(item)}
                            trigger="click"
                            visible={
                              isPopoverVisible && selectedItem === item.id
                            }
                            onVisibleChange={(e) => {
                              setIsPopoverVisible(e);
                              setSelectedItem(item.id);
                            }}>
                            <div style={{cursor: 'pointer', display: 'flex'}}>
                              <DotMenuSide
                                color={
                                  isPopoverVisible && selectedItem === item.id
                                    ? 'var(--primary)'
                                    : 'var(--text-color-2)'
                                }
                              />
                            </div>
                          </Popover>
                        </ContentWrapper>
                      </DraggableItem>
                    )}
                  </Draggable>
                ))}
              {provided.placeholder}
            </div>
          )}
        </Droppable>
      </DragDropContext>
    </Modal>
  );
};

const ItemName = styled.div`
  width: 160px;
`;

const DataType = styled.div`
  width: 72px;
`;

const Display = styled.div`
  width: 33px;
`;

const Bold = styled.span`
  font-weight: 700;
`;

const Gap = styled.div<{width: number}>`
  width: ${({width}) => `${width}px`};
  height: 100%;
`;

const ContentWrapper = styled.div`
  display: flex;
`;

const HeaderContentWrapper = styled.div`
  display: flex;
  margin-bottom: 10px;
`;

const DraggableItem = styled.div`
  min-height: 20px;
  display: flex;
  align-items: center;
  background-color: #fff;
  border-left: solid 3px #fff;
  cursor: pointer;
  margin-bottom: 10px;
  &.dragging {
    background-color: #f3f9ff;
    box-shadow: 0 0 10px rgba(0, 0, 0, 0.13);
    border-left: solid 3px var(--primary);
  }
`;

const HandleWrapper = styled.div`
  display: flex;
  align-items: center;
  svg {
    width: 30px;
    height: 24px;
  }
`;

const PopoverContent = styled.div`
  .ant-popover-placement-bottomLeft {
    padding-top: 0;
  }
`;

const PopoverItem = styled.div`
  display: flex;
  align-items: center;
  padding-left: 16px;
  width: 86px;
  height: 43px;
  cursor: pointer;
  font-weight: 500;
  font-size: 14px;

  &:hover {
    background: #f3f5f7;
  }
`;

const TrashWrapper = styled.div`
  padding-top: 5px;
`;
